import { AllFilters } from './components/AllFilters/AllFilters';
import { CategoryFilter } from './components/CategoryFilter/CategoryFilter';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { CurrentOpenMenu } from '@/pages/SearchPageSimplified/types';
import { Distance } from '@/types/Distance';
import { fetchSearchAggregationsOnDemand } from '@/redux/modules/search/search.actions';
import { isArchivedSearch, parseQueryParams } from '@/utils/queryParams';
import { KeywordFilter } from './components/KeywordFilter/KeywordFilter';
import { LocationFilter } from './components/LocationFilter/LocationFilter';
import { RatingFilter } from './components/RatingFilter/RatingFilter';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { ShippingFilter } from './components/ShippingFilter/ShippingFilter';
import { SortFilter } from './components/SortFilter/SortFilter';
import { useAppDispatch } from '@/redux/hooks';
import { useCallback, useRef, useState } from 'react';
import { useDragToScroll } from './hooks/useDragToScroll';
import { useLocation } from 'react-router-dom';
import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';

type Props = {
    distance: Distance;
    searchTerm: string;
};

export const SearchFilters = ({ distance, searchTerm }: Props) => {
    const { pathname, search, state } = useLocation();
    const queryParams = parseQueryParams(search);
    const dispatch = useAppDispatch();

    const [currentOpenMenu, setCurrentOpenMenu] = useState<CurrentOpenMenu | undefined>(
        typeof state === 'object' ? state?.currentOpenMenu : undefined
    );
    const [showAllFiltersDrawer, setShowAllFiltersDrawer] = useState(currentOpenMenu === CurrentOpenMenu.AllFilters);

    const isMobile = useWindowIsMobile();

    const containerRef = useRef<HTMLDivElement>(null);

    const { isDragging } = useDragToScroll(containerRef, { disableScroll: showAllFiltersDrawer });

    const archivedSearch = isArchivedSearch(queryParams, pathname);

    const handleMenuOpen = useCallback(
        (menu: CurrentOpenMenu) => {
            if (archivedSearch && (menu === CurrentOpenMenu.Category || menu === CurrentOpenMenu.Location)) {
                dispatch(fetchSearchAggregationsOnDemand());
            }
            setCurrentOpenMenu(menu);
        },
        [archivedSearch, dispatch]
    );

    const permittedSortOptions = !archivedSearch
        ? [
              SearchSortAndDirection.MostRelevant,
              SearchSortAndDirection.TimeMostRecent,
              SearchSortAndDirection.NewlyListed,
              SearchSortAndDirection.PriceLowest,
              SearchSortAndDirection.PriceHighest,
          ]
        : undefined;

    if (isMobile) {
        return (
            <AllFilters
                currentOpenMenu={currentOpenMenu}
                distance={distance}
                isArchivedSearch={archivedSearch}
                onMenuClose={() => setCurrentOpenMenu(undefined)}
                onMenuOpen={setCurrentOpenMenu}
                permittedSortOptions={permittedSortOptions}
                setShowAllFiltersDrawer={setShowAllFiltersDrawer}
                showAllFiltersDrawer={showAllFiltersDrawer}
            />
        );
    }

    return (
        <div
            className={cn(
                'relative m-auto flex max-w-[1644px] items-start justify-between gap-md bg-background-primary py-md',
                {
                    'border-t-0': !Boolean(searchTerm),
                }
            )}
            data-testid="search-filters-container"
        >
            <KeywordFilter />
            <div
                className={cn(
                    'flex w-full items-center gap-xs overflow-x-auto',
                    '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                    'select-none',
                    {
                        'cursor-grabbing': isDragging,
                        'overflow-x-hidden': showAllFiltersDrawer,
                    }
                )}
                data-testid="scroll-container"
                ref={containerRef}
            >
                <AllFilters
                    currentOpenMenu={currentOpenMenu}
                    distance={distance}
                    isArchivedSearch={archivedSearch}
                    onMenuClose={() => setCurrentOpenMenu(undefined)}
                    onMenuOpen={setCurrentOpenMenu}
                    setShowAllFiltersDrawer={setShowAllFiltersDrawer}
                    showAllFiltersDrawer={showAllFiltersDrawer}
                />
                <CategoryFilter
                    currentOpenMenu={currentOpenMenu}
                    isArchivedSearch={archivedSearch}
                    onMenuClose={() => setCurrentOpenMenu(undefined)}
                    onMenuOpen={handleMenuOpen}
                />

                <LocationFilter
                    currentOpenMenu={currentOpenMenu}
                    distance={distance}
                    isArchivedSearch={archivedSearch}
                    onMenuClose={() => setCurrentOpenMenu(undefined)}
                    onMenuOpen={handleMenuOpen}
                />
                <ShippingFilter
                    currentOpenMenu={currentOpenMenu}
                    isArchivedSearch={archivedSearch}
                    onMenuClose={() => setCurrentOpenMenu(undefined)}
                    onMenuOpen={setCurrentOpenMenu}
                />
                <RatingFilter isArchivedSearch={archivedSearch} />
            </div>
            <SortFilter permittedSortOptions={permittedSortOptions} />
        </div>
    );
};
